import {
  Box,
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Paper,
  Button,
} from "@mantine/core";
import {
  IconSparkles,
  IconCheck,
  IconLoader2,
  IconArrowRight,
} from "@tabler/icons-react";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { useEffect, useState } from "react";
import { findList } from "../../../../../api/ContactAPI";

// ---------------------------------------------------------

const ANALYSIS_STEPS = [
  "Lecture des profils de contacts...",
  "Compréhension de l'audience...",
  "Détection des signaux d'intention...",
  "Préparation du message personnalisé...",
];

const AnalysisWidget = ({ lists, setActive }) => {
  const listIds = lists || [];

  // États du widget
  const [status, setStatus] = useState("waiting"); // 'waiting' | 'analyzing' | 'completed'
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActive("strategy");
  };

  // 1. Détecter l'ajout d'une liste pour lancer l'analyse
  useEffect(() => {
    if (listIds.length > 0) {
      setStatus("analyzing");
      setActiveStep(0);
    }
  }, [listIds.length]);

  // 2. Simuler l'avancement des étapes
  useEffect(() => {
    if (status === "analyzing") {
      if (activeStep >= ANALYSIS_STEPS.length) {
        // Petite pause avant d'afficher le succès final
        const finalTimer = setTimeout(() => setStatus("completed"), 200);
        return () => clearTimeout(finalTimer);
      }

      // Temps aléatoire entre 1s et 2.5s pour chaque étape
      const delay = 200 + Math.random() * 600;
      const timer = setTimeout(() => {
        setActiveStep((prev) => prev + 1);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [status, activeStep]);

  // --- RENDU : ÉTAT 1 (En attente) ---
  if (status === "waiting") {
    return (
      <Card withBorder radius="xl" p="xl" shadow="lg" flex={1}>
        <Stack
          align="center"
          justify="center"
          ta="center"
          gap="sm"
          py="md"
          mih={400}
        >
          <Box
            w={64}
            h={64}
            bg="violet.0"
            style={{
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
            }}
          >
            <IconSparkles color="var(--mantine-color-violet-5)" size={32} />
          </Box>
          <Text fw={700} size="lg" mt="xs">
            Analyse IA en attente
          </Text>
          <Text c="dimmed" size="sm" maw={300} mx="auto">
            Sélectionnez une liste pour lancer l'analyse automatique de votre
            audience.
          </Text>
        </Stack>
      </Card>
    );
  }

  // --- RENDU : ÉTAT 2 & 3 (Analyse en cours / Terminée) ---
  const isCompleted = status === "completed";

  return (
    <Card withBorder radius="xl" p="xl" shadow="lg" flex={1} mih={400}>
      {/* En-tête dynamique */}
      <Group gap="sm" mb="xl">
        <IconSparkles color="var(--mantine-color-violet-5)" size={24} />
        <Text fw={700} size="lg">
          {isCompleted ? "Analyse terminée" : "Analyse de l'audience"}
        </Text>
      </Group>

      {/* Liste des étapes */}
      <Stack gap="md">
        {ANALYSIS_STEPS.map((stepLabel, index) => {
          const isDone = index < activeStep || isCompleted;
          const isCurrent = index === activeStep && !isCompleted;

          return (
            <Group key={index} gap="md" wrap="nowrap">
              {/* Icône de statut */}
              <Box
                w={24}
                h={24}
                style={{ display: "grid", placeItems: "center", flexShrink: 0 }}
              >
                {isDone ? (
                  <ThemeIcon color="teal" size={24} radius="xl">
                    <IconCheck size={14} stroke={3} />
                  </ThemeIcon>
                ) : isCurrent ? (
                  <IconLoader2
                    size={24}
                    color="var(--mantine-color-violet-4)"
                    style={{ animation: "spin 2s linear infinite" }}
                  />
                ) : (
                  <Box
                    w={24}
                    h={24}
                    bg="gray.1"
                    style={{
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <Box
                      w={6}
                      h={6}
                      bg="gray.4"
                      style={{ borderRadius: "50%" }}
                    />
                  </Box>
                )}
              </Box>

              {/* Texte de l'étape */}
              <Text
                size="md"
                fw={isDone || isCurrent ? 500 : 400}
                c={isDone || isCurrent ? "dark" : "dimmed"}
              >
                {stepLabel}
              </Text>
            </Group>
          );
        })}
      </Stack>

      {/* Boîte de succès finale */}
      {isCompleted && (
        <Paper
          mt="xl"
          p="md"
          radius="lg"
          bg="teal.0" // Fond vert très clair
          shadow="none"
          style={{ border: "1px solid var(--mantine-color-teal-2)" }}
        >
          <Text fw={700} c="dark" mb={4}>
            Votre audience est prête.
          </Text>
          <Text size="md" lh={1.5}>
            Votre agent a analysé votre audience et détecté les signaux
            d'intention. Il adaptera le message et personnalisera chaque
            interaction pour chaque prospect.
          </Text>
        </Paper>
      )}

      {isCompleted && (
        <Group justify="center" mt={"xl"}>
          <Button
            color="primary"
            radius="xl"
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleNext}
          >
            Continuer
          </Button>
        </Group>
      )}

      {/* Styles CSS pour l'animation de rotation du loader */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default AnalysisWidget;
